import type {
  ServerNotification,
  ServerRequest,
} from '@modelcontextprotocol/sdk/types.js';
import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { RequestHandlerExtra } from '@modelcontextprotocol/sdk/shared/protocol.js';
import tracer from 'dd-trace';
import chalk from 'chalk';
import output from '../../utils/output-writer';
import { createCallToolResult, createTextContent } from './output-parser';

type RegisterToolFuncType = McpServer['registerTool'];

export const wrapRegister: (server: McpServer) => RegisterToolFuncType =
  (server: McpServer) => (name, config: object, handler) => {
    const wrappedHandler = async (
      args: Parameters<typeof handler>[0],
      extra: RequestHandlerExtra<ServerRequest, ServerNotification>
    ) => {
      const span = tracer.startSpan('cli.run');
      let result;
      return tracer.scope().activate(span, async () => {
        const traceId = span.context().toTraceId();
        try {
          const handlerResult = await handler(args, extra);
          result = createCallToolResult({
            contentBlocks: [
              ...handlerResult.content,
              createTextContent(`Trace ID: ${traceId}`),
            ],
          });
        } catch (error) {
          output.error(
            chalk.red('✗ Calling tool failed:'),
            error instanceof Error ? error.message : String(error)
          );

          result = createCallToolResult({
            contentBlocks: [
              createTextContent(
                error instanceof Error ? error.message : String(error)
              ),
              createTextContent(`Trace ID: ${traceId}`),
            ],
            isError: true,
          });
        }
        span.finish();
        return result;
      });
    };

    return server.registerTool(name, config, wrappedHandler);
  };

export type WrappedRegisterToolFuncType = ReturnType<typeof wrapRegister>;

export interface ToolDefinition {
  setup: (register: WrappedRegisterToolFuncType) => void;
}
